import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import { useHoaRole } from './features/hoa/hooks';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Homie = lazy(() => import('./pages/Homie'));
const Requests = lazy(() => import('./pages/Requests'));
const ServiceRequest = lazy(() => import('./pages/ServiceRequest'));
const Bookings = lazy(() => import('./pages/Bookings'));
const Chat = lazy(() => import('./pages/Chat'));
const Profile = lazy(() => import('./pages/Profile'));
const BookingConfirmation = lazy(() => import('./pages/BookingConfirmation'));
const SearchingPros = lazy(() => import('./pages/SearchingPros'));
const BidsReceived = lazy(() => import('./pages/BidsReceived'));
const ProDashboard = lazy(() => import('./pages/pro/ProDashboard'));
const ProLeads = lazy(() => import('./pages/pro/ProLeads'));
const ProLeadDetail = lazy(() => import('./pages/pro/ProLeadDetail'));
const JobDetail = lazy(() => import('./pages/pro/JobDetail'));
const MyJobs = lazy(() => import('./pages/pro/MyJobs'));
const ProProfile = lazy(() => import('./pages/pro/ProProfile'));
const Verification = lazy(() => import('./pages/pro/Verification'));
const ProJobDetail = lazy(() => import('./pages/pro/ProJobDetail'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const AuthAction = lazy(() => import('./pages/AuthAction'));
const RoleSelection = lazy(() => import('./pages/onboarding/RoleSelection'));
const HomeownerOnboarding = lazy(() => import('./pages/onboarding/HomeownerOnboarding'));
const ProOnboarding = lazy(() => import('./pages/onboarding/ProOnboarding'));

// ─── HOA feature ─────────────────────────────────────────────────────
const HoaLanding = lazy(() => import('./features/hoa/pages/HoaLanding'));
const HoaEvents = lazy(() => import('./features/hoa/pages/HoaEvents'));
const HoaChat = lazy(() => import('./features/hoa/pages/HoaChat'));
const HoaRequests = lazy(() => import('./features/hoa/pages/HoaRequests'));
const HoaBilling = lazy(() => import('./features/hoa/pages/HoaBilling'));
const JoinHoa = lazy(() => import('./features/hoa/pages/JoinHoa'));
const HoaAdminDashboard = lazy(() => import('./features/hoa/pages/HoaAdminDashboard'));

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-brand-red border-t-transparent rounded-full animate-spin" />
  </div>
);

// HOA adminship is NOT a value of users/{uid}.role. It is a per-community fact
// on hoa_communities/{cid}/members/{uid}.hoa_role — see useHoaRole. So none of
// the three gates below branch on 'hoa_admin' any more: a board admin is a
// homeowner who also administers a community, lands on /dashboard like any
// homeowner, reads /hoa as the resident they are, and enters /hoa-admin from
// the AdminBanner. Sending them to /hoa-admin from here would also have
// deadlocked — HoaAdminRoute now asks the junction, so a user still carrying
// role 'hoa_admin' without a board_admin junction doc would be bounced to
// /dashboard and straight back again, forever.

function RoleRedirect() {
  const { profile, profileLoading } = useAuth();
  if (profileLoading || profile === undefined) return <Spinner />;
  if (profile?.role === 'pro') return <Navigate to="/pro/dashboard" replace />;
  return <Navigate to="/dashboard" replace />;
}

function ProRoute({ children }) {
  const { profile, profileLoading } = useAuth();
  if (profileLoading || profile === undefined) return <Spinner />;
  if (profile?.role !== 'pro') return <Navigate to="/dashboard" replace />;
  return children;
}

function HomeownerRoute({ children }) {
  const { profile, profileLoading } = useAuth();
  if (profileLoading || profile === undefined) return <Spinner />;
  if (profile?.role === 'pro') return <Navigate to="/pro/dashboard" replace />;
  return children;
}

function HoaAdminRoute({ children }) {
  const { profile, profileLoading } = useAuth();
  const { role, loading: roleLoading } = useHoaRole();
  // The junction is a Firestore read. Redirecting before it resolves would
  // bounce every real admin off the dashboard on first paint.
  if (profileLoading || profile === undefined || roleLoading) return <Spinner />;
  if (role !== 'board_admin') {
    const target = profile?.role === 'pro' ? '/pro/dashboard' : '/dashboard';
    return <Navigate to={target} replace />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <BrowserRouter>
          <Suspense fallback={<Spinner />}>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/auth/action" element={<AuthAction />} />
                  <Route path="/onboarding" element={<ProtectedRoute><RoleSelection /></ProtectedRoute>} />
                  <Route path="/onboarding/homeowner" element={<ProtectedRoute><HomeownerOnboarding /></ProtectedRoute>} />
                  <Route path="/onboarding/pro" element={<ProtectedRoute><ProOnboarding /></ProtectedRoute>} />
                  <Route path="/verify" element={<ProtectedRoute><Verification /></ProtectedRoute>} />

                  {/* HOA join flow — outside Layout so it renders full-screen */}
                  <Route path="/join-hoa" element={<ProtectedRoute><JoinHoa /></ProtectedRoute>} />

                  <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                    <Route index element={<RoleRedirect />} />

                    {/* Homeowner-only routes */}
                    <Route path="dashboard" element={<HomeownerRoute><Dashboard /></HomeownerRoute>} />
                    <Route path="homie" element={<HomeownerRoute><Homie /></HomeownerRoute>} />
                    <Route path="requests" element={<HomeownerRoute><Requests /></HomeownerRoute>} />
                    <Route path="service-request" element={<HomeownerRoute><ServiceRequest /></HomeownerRoute>} />
                    <Route path="bookings" element={<HomeownerRoute><Bookings /></HomeownerRoute>} />
                    <Route path="booking-confirmation" element={<HomeownerRoute><BookingConfirmation /></HomeownerRoute>} />
                    <Route path="searching" element={<HomeownerRoute><SearchingPros /></HomeownerRoute>} />
                    <Route path="bids" element={<HomeownerRoute><BidsReceived /></HomeownerRoute>} />

                    {/* HOA resident routes */}
                    <Route path="hoa"          element={<HomeownerRoute><HoaLanding /></HomeownerRoute>} />
                    <Route path="hoa/events"   element={<HomeownerRoute><HoaEvents /></HomeownerRoute>} />
                    <Route path="hoa/chat"     element={<HomeownerRoute><HoaChat /></HomeownerRoute>} />
                    <Route path="hoa/requests" element={<HomeownerRoute><HoaRequests /></HomeownerRoute>} />
                    <Route path="hoa/billing"  element={<HomeownerRoute><HoaBilling /></HomeownerRoute>} />

                    {/* HOA admin routes */}
                    <Route path="hoa-admin" element={<HoaAdminRoute><HoaAdminDashboard /></HoaAdminRoute>} />

                    {/* Shared routes */}
                    <Route path="chat" element={<Chat />} />
                    <Route path="profile" element={<Profile />} />

                    {/* Pro-only routes */}
                    <Route path="pro/dashboard" element={<ProRoute><ProDashboard /></ProRoute>} />
                    <Route path="pro/leads" element={<ProRoute><ProLeads /></ProRoute>} />
                    <Route path="pro/leads/:requestId" element={<ProRoute><ProLeadDetail /></ProRoute>} />
                    <Route path="pro/job-detail" element={<ProRoute><ProJobDetail /></ProRoute>} />
                    <Route path="pro/jobs" element={<ProRoute><MyJobs /></ProRoute>} />
                    <Route path="pro/chat" element={<ProRoute><Chat /></ProRoute>} />
                    <Route path="pro/profile" element={<ProRoute><ProProfile /></ProRoute>} />
                  </Route>
                </Routes>
            </Suspense>
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
  );
}

export default App;
